/**
 * Centralized React Query configuration for the application
 *
 * This file provides tiered caching strategies based on data volatility:
 * - STATIC: Data that rarely changes (filter options, sensors, field configs)
 * - SEMI_STATIC: Data that changes occasionally (stats, analytics)
 * - DYNAMIC: Data that changes frequently (event lists, violations)
 * - REALTIME: Data that needs near-real-time updates (dashboard widgets)
 *
 * Note: This project uses @tanstack/react-query v4, which uses `cacheTime`.
 * React Query v5 renamed this to `gcTime`.
 */

/**
 * Time constants in milliseconds for clarity
 */
const SECONDS = 1000;
const MINUTES = 60 * SECONDS;

/**
 * Query configuration options for React Query v4
 */
export interface QueryConfigOptions {
  /** Time until data is considered stale (in ms) */
  staleTime: number;
  /** Time until inactive query data is garbage collected (in ms) */
  cacheTime: number;
  /** Whether to refetch when window gains focus */
  refetchOnWindowFocus: boolean;
  /** Whether to refetch when component mounts */
  refetchOnMount: boolean | 'always';
  /** Keep previous data while fetching new data */
  keepPreviousData?: boolean;
}

/**
 * Data category types for different caching strategies
 */
export type DataCategory = 'STATIC' | 'SEMI_STATIC' | 'DYNAMIC' | 'REALTIME';

/**
 * Tiered query configurations based on data volatility
 */
export const QUERY_CONFIG: Record<DataCategory, QueryConfigOptions> = {
  /**
   * STATIC - Data that rarely changes
   * Examples: filter options, sensor lists, field configurations, violation types
   * staleTime: 10 minutes, cacheTime: 15 minutes
   */
  STATIC: {
    staleTime: 10 * MINUTES,
    cacheTime: 15 * MINUTES,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    keepPreviousData: true,
  },

  /**
   * SEMI_STATIC - Data that changes occasionally
   * Examples: statistics, analytics summaries, aggregated data
   * staleTime: 2-5 minutes, cacheTime: 10 minutes
   */
  SEMI_STATIC: {
    staleTime: 5 * MINUTES,
    cacheTime: 10 * MINUTES,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    keepPreviousData: true,
  },

  /**
   * DYNAMIC - Data that changes frequently
   * Examples: event lists, violation lists, paginated data
   * staleTime: 30s-1 minute, cacheTime: 5 minutes
   */
  DYNAMIC: {
    staleTime: 1 * MINUTES,
    cacheTime: 5 * MINUTES,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    keepPreviousData: true,
  },

  /**
   * REALTIME - Data that needs near-real-time updates
   * Examples: dashboard widgets, live counters, active alerts
   * staleTime: 10-30 seconds, cacheTime: 2 minutes
   */
  REALTIME: {
    staleTime: 30 * SECONDS,
    cacheTime: 2 * MINUTES,
    refetchOnWindowFocus: true,
    refetchOnMount: 'always',
    keepPreviousData: true,
  },
} as const;

/**
 * Helper function to get query configuration by data category
 *
 * @param category - The data category (STATIC, SEMI_STATIC, DYNAMIC, REALTIME)
 * @returns Query configuration options for the specified category
 *
 * @example
 * ```ts
 * const { data } = api.filterConfig.getConfig.useQuery(
 *   { moduleName },
 *   getQueryConfig('STATIC')
 * );
 * ```
 */
export function getQueryConfig(category: DataCategory): QueryConfigOptions {
  return { ...QUERY_CONFIG[category] };
}

/**
 * Helper function to get query configuration with custom overrides
 *
 * @param category - The data category (STATIC, SEMI_STATIC, DYNAMIC, REALTIME)
 * @param overrides - Custom options to override the default configuration
 * @returns Merged query configuration options
 *
 * @example
 * ```ts
 * const { data } = api.traffic.violations.getViolations.useQuery(
 *   filters,
 *   getQueryConfigWithOverrides('DYNAMIC', { staleTime: 45 * 1000 })
 * );
 * ```
 */
export function getQueryConfigWithOverrides(
  category: DataCategory,
  overrides: Partial<QueryConfigOptions>,
): QueryConfigOptions {
  return { ...QUERY_CONFIG[category], ...overrides };
}

/**
 * Default QueryClient options for the application
 * These provide sensible defaults that can be overridden per-query
 */
export const DEFAULT_QUERY_CLIENT_OPTIONS = {
  queries: {
    staleTime: 30 * SECONDS,
    cacheTime: 5 * MINUTES,
    refetchOnWindowFocus: false,
    retry: 1,
  },
} as const;

/**
 * Time constants exported for use in custom configurations
 */
export const TIME = {
  SECONDS,
  MINUTES,
} as const;

/**
 * ============================================================================
 * PREFETCHING UTILITIES
 * ============================================================================
 *
 * These utilities enable prefetching of common static data on app initialization
 * to improve perceived performance. Static data like filter options, field configs,
 * and sensor lists are fetched in the background after authentication.
 */

/**
 * Module names that support filter configuration
 */
export type FilterModuleName = 'traffic_events' | 'seatbelt_violations';

/**
 * View types for field configuration
 */
export type FieldViewType = 'table' | 'grid' | 'filter';

/**
 * Configuration for what data to prefetch
 */
export interface PrefetchConfig {
  /** Filter config modules to prefetch */
  filterModules?: FilterModuleName[];
  /** Field config combinations to prefetch (module + viewType) */
  fieldConfigs?: Array<{ moduleName: FilterModuleName; viewType: FieldViewType }>;
  /** Whether to prefetch sensor types */
  sensorTypes?: boolean;
  /** Whether to prefetch sensor list (first page) */
  sensorList?: boolean;
}

/**
 * Default prefetch configuration - prefetches commonly used static data
 */
export const DEFAULT_PREFETCH_CONFIG: PrefetchConfig = {
  filterModules: ['traffic_events', 'seatbelt_violations'],
  fieldConfigs: [
    { moduleName: 'traffic_events', viewType: 'table' },
    { moduleName: 'traffic_events', viewType: 'filter' },
    { moduleName: 'seatbelt_violations', viewType: 'table' },
    { moduleName: 'seatbelt_violations', viewType: 'filter' },
  ],
  sensorTypes: true,
  sensorList: false, // Disabled by default as sensor list can be large
};

/**
 * Prefetch static data using the tRPC client and QueryClient
 *
 * This function prefetches commonly used static data in the background
 * after app initialization. It uses the STATIC query configuration
 * to ensure data is cached for 10+ minutes.
 *
 * @param queryClient - The React Query QueryClient instance
 * @param trpcClient - The tRPC proxy client for making queries
 * @param config - Optional configuration for what to prefetch
 * @returns Promise that resolves when all prefetches complete (or fail gracefully)
 *
 * @example
 * ```ts
 * import { queryClient, trpcClient } from '@/utils/api-client';
 * import { prefetchStaticData } from '@/lib/query-config';
 *
 * // In your auth success handler or app initialization:
 * await prefetchStaticData(queryClient, trpcClient);
 * ```
 */
export async function prefetchStaticData(
  queryClient: import('@tanstack/react-query').QueryClient,
  trpcClient: ReturnType<typeof import('@trpc/client').createTRPCProxyClient<any>>,
  config: PrefetchConfig = DEFAULT_PREFETCH_CONFIG,
): Promise<{ success: boolean; errors: string[] }> {
  const errors: string[] = [];
  const prefetchPromises: Promise<void>[] = [];

  // Prefetch filter options for each module
  if (config.filterModules && config.filterModules.length > 0) {
    for (const moduleName of config.filterModules) {
      prefetchPromises.push(
        queryClient
          .prefetchQuery({
            queryKey: [['filterConfig', 'getFilterOptions'], { input: { moduleName }, type: 'query' }],
            queryFn: async () => {
              return await (trpcClient as any).filterConfig.getFilterOptions.query({ moduleName });
            },
            staleTime: QUERY_CONFIG.STATIC.staleTime,
            cacheTime: QUERY_CONFIG.STATIC.cacheTime,
          } as any)
          .catch((error: Error) => {
            const errorMsg = `Failed to prefetch filter options for ${moduleName}: ${error.message}`;
            errors.push(errorMsg);
            // Log in development only
            if (import.meta.env.MODE === 'development') {
              console.warn(`[Prefetch] ${errorMsg}`);
            }
          }),
      );
    }
  }

  // Prefetch field configs for each module/viewType combination
  if (config.fieldConfigs && config.fieldConfigs.length > 0) {
    for (const { moduleName, viewType } of config.fieldConfigs) {
      prefetchPromises.push(
        queryClient
          .prefetchQuery({
            queryKey: [
              ['fieldConfig', 'getConfig'],
              { input: { moduleName, viewType }, type: 'query' },
            ],
            queryFn: async () => {
              return await (trpcClient as any).fieldConfig.getConfig.query({ moduleName, viewType });
            },
            staleTime: QUERY_CONFIG.STATIC.staleTime,
            cacheTime: QUERY_CONFIG.STATIC.cacheTime,
          } as any)
          .catch((error: Error) => {
            const errorMsg = `Failed to prefetch field config for ${moduleName}/${viewType}: ${error.message}`;
            errors.push(errorMsg);
            if (import.meta.env.MODE === 'development') {
              console.warn(`[Prefetch] ${errorMsg}`);
            }
          }),
      );
    }
  }

  // Prefetch sensor types (static reference data)
  if (config.sensorTypes) {
    prefetchPromises.push(
      queryClient
        .prefetchQuery({
          queryKey: [['sensors', 'getTypes'], { type: 'query' }],
          queryFn: async () => {
            return await (trpcClient as any).sensors.getTypes.query();
          },
          staleTime: QUERY_CONFIG.STATIC.staleTime,
          cacheTime: QUERY_CONFIG.STATIC.cacheTime,
        } as any)
        .catch((error: Error) => {
          const errorMsg = `Failed to prefetch sensor types: ${error.message}`;
          errors.push(errorMsg);
          if (import.meta.env.MODE === 'development') {
            console.warn(`[Prefetch] ${errorMsg}`);
          }
        }),
    );
  }

  // Prefetch sensor list (first page) - useful for dropdowns and selectors
  if (config.sensorList) {
    prefetchPromises.push(
      queryClient
        .prefetchQuery({
          queryKey: [
            ['sensors', 'getAll'],
            { input: { skip: 0, take: 50, sortBy: 'name', sortOrder: 'asc' }, type: 'query' },
          ],
          queryFn: async () => {
            return await (trpcClient as any).sensors.getAll.query({
              skip: 0,
              take: 50,
              sortBy: 'name',
              sortOrder: 'asc',
            });
          },
          staleTime: QUERY_CONFIG.STATIC.staleTime,
          cacheTime: QUERY_CONFIG.STATIC.cacheTime,
        } as any)
        .catch((error: Error) => {
          const errorMsg = `Failed to prefetch sensor list: ${error.message}`;
          errors.push(errorMsg);
          if (import.meta.env.MODE === 'development') {
            console.warn(`[Prefetch] ${errorMsg}`);
          }
        }),
    );
  }

  // Wait for all prefetches to complete (they fail gracefully)
  await Promise.all(prefetchPromises);

  // Log summary in development
  if (import.meta.env.MODE === 'development') {
    const totalPrefetches = prefetchPromises.length;
    const successCount = totalPrefetches - errors.length;
    console.log(
      `[Prefetch] Completed: ${successCount}/${totalPrefetches} prefetches successful`,
    );
  }

  return {
    success: errors.length === 0,
    errors,
  };
}

/**
 * Create a prefetch function bound to specific QueryClient and tRPC client
 *
 * This is useful when you want to create a prefetch function once and reuse it,
 * or when you need to pass it around without carrying the dependencies.
 *
 * @param queryClient - The React Query QueryClient instance
 * @param trpcClient - The tRPC proxy client for making queries
 * @returns A function that prefetches static data with optional config
 *
 * @example
 * ```ts
 * const prefetch = createPrefetchFunction(queryClient, trpcClient);
 *
 * // Later, on auth success:
 * useEffect(() => {
 *   if (isAuthenticated) {
 *     prefetch();
 *   }
 * }, [isAuthenticated]);
 * ```
 */
export function createPrefetchFunction(
  queryClient: import('@tanstack/react-query').QueryClient,
  trpcClient: ReturnType<typeof import('@trpc/client').createTRPCProxyClient<any>>,
): (config?: PrefetchConfig) => Promise<{ success: boolean; errors: string[] }> {
  return (config?: PrefetchConfig) => prefetchStaticData(queryClient, trpcClient, config);
}

/**
 * Query key generators for tRPC queries
 *
 * These match the query key structure used by @trpc/react-query v10
 * and can be used for manual cache operations like invalidation or prefetching.
 */
export const QUERY_KEYS = {
  /** Filter config query keys */
  filterConfig: {
    getFilterOptions: (moduleName: FilterModuleName) => [
      ['filterConfig', 'getFilterOptions'],
      { input: { moduleName }, type: 'query' },
    ],
    getConfig: (moduleName: FilterModuleName) => [
      ['filterConfig', 'getConfig'],
      { input: { moduleName }, type: 'query' },
    ],
  },
  /** Field config query keys */
  fieldConfig: {
    getConfig: (moduleName: FilterModuleName, viewType: FieldViewType) => [
      ['fieldConfig', 'getConfig'],
      { input: { moduleName, viewType }, type: 'query' },
    ],
  },
  /** Sensor query keys */
  sensors: {
    getTypes: () => [['sensors', 'getTypes'], { type: 'query' }],
    getAll: (params?: { skip?: number; take?: number; sortBy?: string; sortOrder?: string }) => [
      ['sensors', 'getAll'],
      { input: params || { skip: 0, take: 50, sortBy: 'name', sortOrder: 'asc' }, type: 'query' },
    ],
  },
} as const;
